import { FC, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Toggle } from "components/inputs/Toggle"

import { settingsService } from "services/settings/settings.service"
import { BetsTablesConfig } from "services/settings/type"

import { SettingsHistoryNote } from "./SettingsHistoryNote"
import { SettingsSectionActions } from "./SettingsSectionActions"

const TABLE_ROWS: Array<{ key: keyof BetsTablesConfig; tKey: string }> = [
  { key: "winBets", tKey: "liveWins" },
  { key: "userBets", tKey: "userBets" },
  { key: "allBets", tKey: "latestBets" },
  { key: "highRollerBets", tKey: "highRollers" }
]

interface BetsTablesSectionProps {
  betsTables: BetsTablesConfig
  currencies: Record<string, boolean>
  onChange: (betsTables: BetsTablesConfig) => void
  onRefresh: () => Promise<void>
}

export const BetsTablesSection: FC<BetsTablesSectionProps> = ({ betsTables, currencies, onChange, onRefresh }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [saving, setSaving] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)

  const currencyOptions = Object.keys(currencies)

  const handleSave = async () => {
    setSaving(true)
    try {
      const success = await settingsService.updateBetsTables(betsTables)
      if (success) {
        toast.success(t("settings.general.betsTables.updated") as string)
        setHistoryKey((key) => key + 1)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleResetDone = async () => {
    await onRefresh()
    setHistoryKey((key) => key + 1)
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h4 className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">{t("settings.general.betsTables.sectionTitle")}</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t("settings.general.betsTables.sectionDescription")}</p>
        <SettingsHistoryNote target="bets_tables" refreshKey={historyKey} />
      </div>

      <div className="flex flex-col max-w-2xl rounded-lg border border-black/10 dark:border-white/10 overflow-hidden">
        {TABLE_ROWS.map(({ key, tKey }) => (
          <div
            key={key}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 px-4 py-3 border-b border-black/10 dark:border-white/10 last:border-b-0 bg-white dark:bg-white/[0.04]"
          >
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-sm font-medium text-gray-800 dark:text-white">{t(`settings.general.betsTables.${tKey}.label`)}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t(`settings.general.betsTables.${tKey}.description`)}</span>
            </div>
            <div className="flex items-center gap-4 sm:shrink-0">
              <select
                value={betsTables[key].currency}
                onChange={(e) => onChange({ ...betsTables, [key]: { ...betsTables[key], currency: e.target.value } })}
                disabled={saving}
                className="text-sm rounded-md border border-black/20 dark:border-white/20 bg-white dark:bg-[#0C0C1A] text-gray-800 dark:text-white px-2 py-1 disabled:opacity-50"
              >
                {currencyOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <Toggle
                checked={betsTables[key].enabled}
                onChange={() => onChange({ ...betsTables, [key]: { ...betsTables[key], enabled: !betsTables[key].enabled } })}
                disabled={saving}
              />
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 max-w-2xl">ℹ️ {t("settings.general.betsTables.hiddenNotice")}</p>

      <SettingsSectionActions
        onSave={handleSave}
        saving={saving}
        resetTarget="bets_tables"
        resetLabel={t("settings.general.betsTables.sectionTitle")}
        onResetDone={handleResetDone}
      />
    </div>
  )
}
